import React, { useLayoutEffect, useMemo, useRef } from 'react';
import { clsx, type ClassValue } from 'clsx';
import { twMerge } from 'tailwind-merge';
import { Globe, Search } from 'lucide-react';
import type { DetrixSession } from '../../types';
import { usePlaybackStore } from '../../store/usePlaybackStore';
import { useSessionUiStore } from '../../store/useSessionUiStore';
import { getErrorCorrelation, isSelectionError } from '../../utils/sessionErrorSignals';
import { formatDuration, formatSessionOffset } from '../../utils/time';
import {
  filterUnifiedNetworkRows,
  mergeUnifiedNetworkRows,
  PAGE_CAPTURE_TYPE,
  type UnifiedNetworkRow,
} from '../../utils/networkRowFilter';

function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

function statusPillClass(status: number): string {
  if (status >= 200 && status < 300) return 'status-2xx';
  if (status >= 300 && status < 400) return 'status-3xx';
  if (status >= 400 && status < 500) return 'status-4xx';
  return 'status-5xx';
}

function rowContainsPlayhead(row: UnifiedNetworkRow, t: number): boolean {
  const start = row.entry.timestamp;
  const end = start + Math.max(row.entry.timing.durationMs, 1);
  return t >= start && t <= end;
}

function isNetworkError(status: number, err?: unknown): boolean {
  if (err) return true;
  return status >= 400;
}

export const NetworkPanel: React.FC<{ session: DetrixSession }> = ({ session }) => {
  const { network = [], browserResources = [] } = session;
  const { focusedTimestamp, isPlaying } = usePlaybackStore();
  const {
    selectedId,
    setSelectedId,
    activeTab,
    networkUrlFilter,
    networkTypeFilters,
    setNetworkUrlFilter,
    toggleNetworkTypeFilter,
  } = useSessionUiStore();
  const tbodyRef = useRef<HTMLTableSectionElement>(null);

  const merged = useMemo(() => mergeUnifiedNetworkRows(network, browserResources), [network, browserResources]);

  const types = useMemo(() => {
    const set = new Set<string>();
    for (const r of browserResources) set.add(r.resourceType);
    if (network.length > 0) set.add(PAGE_CAPTURE_TYPE);
    return Array.from(set).sort();
  }, [browserResources, network.length]);

  const correlation = useMemo(() => {
    if (!selectedId || !isSelectionError(session, activeTab, selectedId)) return null;
    return getErrorCorrelation(session, activeTab, selectedId);
  }, [session, activeTab, selectedId]);

  const filteredRows = useMemo(
    () => filterUnifiedNetworkRows(merged, networkUrlFilter, networkTypeFilters),
    [merged, networkUrlFilter, networkTypeFilters],
  );

  useLayoutEffect(() => {
    if (focusedTimestamp == null) return;
    const hit = filteredRows.find((row) => rowContainsPlayhead(row, focusedTimestamp));
    if (!hit) return;
    const el = tbodyRef.current?.querySelector(`[data-row-id="${hit.entry.id}"]`);
    el?.scrollIntoView({ block: 'nearest', behavior: isPlaying ? 'auto' : 'smooth' });
  }, [filteredRows, focusedTimestamp, isPlaying]);

  /** While playing only: drive inspector from the playhead so manual row clicks work when paused. */
  useLayoutEffect(() => {
    if (!isPlaying || focusedTimestamp == null) return;
    const hit = filteredRows.find((row) => rowContainsPlayhead(row, focusedTimestamp));
    if (!hit || hit.entry.id === selectedId) return;
    setSelectedId(hit.entry.id);
  }, [filteredRows, focusedTimestamp, isPlaying, selectedId, setSelectedId]);

  useLayoutEffect(() => {
    if (!selectedId || !tbodyRef.current) return;
    const row = filteredRows.find((r) => r.entry.id === selectedId);
    if (!row) return;
    const failed =
      row.kind === 'network'
        ? isNetworkError(row.entry.response?.status ?? 0, row.entry.error)
        : Boolean(row.entry.error) || row.entry.status >= 400;
    if (!failed) return;
    tbodyRef.current.querySelector(`[data-row-id="${selectedId}"]`)?.scrollIntoView({
      block: 'nearest',
      behavior: 'smooth',
    });
  }, [selectedId, filteredRows]);

  if (merged.length === 0) {
    return (
      <div className="flex min-h-0 flex-1 flex-col items-center justify-center text-[var(--text-muted)]">
        <Globe className="mb-2 h-8 w-8 opacity-20" />
        <p>No network activity captured.</p>
      </div>
    );
  }

  return (
    <div className="flex min-h-0 min-w-0 flex-1 flex-col">
      <div className="flex shrink-0 flex-col gap-2 border-b bg-[var(--bg-sidebar)] p-3">
        <div className="relative">
          <Search className="absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-[var(--text-muted)]" />
          <input
            type="text"
            placeholder="Filter by URL..."
            value={networkUrlFilter}
            onChange={(e) => setNetworkUrlFilter(e.target.value)}
            className="w-full rounded border bg-[var(--bg-editor)] py-1.5 pl-8 pr-3 text-[12px] outline-none focus:border-[var(--accent)]"
          />
        </div>
        {types.length > 0 ? (
          <div className="flex flex-wrap gap-1.5">
            {types.map((type) => (
              <button
                key={type}
                type="button"
                onClick={() => toggleNetworkTypeFilter(type)}
                className={cn(
                  'rounded-full px-2 py-0.5 text-[10px] font-medium transition-colors',
                  networkTypeFilters.includes(type)
                    ? 'bg-[var(--accent)] text-[var(--on-accent)]'
                    : 'bg-[var(--bg-elevated)] text-[var(--text-secondary)] hover:bg-[var(--border)]',
                )}
              >
                {type === PAGE_CAPTURE_TYPE ? 'page (fetch/XHR)' : type}
              </button>
            ))}
          </div>
        ) : null}
      </div>

      <div className="min-h-0 flex-1 overflow-auto">
        <table className="data-table">
          <thead>
            <tr>
              <th style={{ width: '72px' }}>Time</th>
              <th style={{ width: '100px' }}>Type</th>
              <th style={{ width: '52px' }}>Method</th>
              <th style={{ width: '56px' }}>Status</th>
              <th>URL</th>
              <th style={{ width: '72px' }}>Duration</th>
            </tr>
          </thead>
          <tbody ref={tbodyRef}>
            {filteredRows.map((row) => {
              const id = row.entry.id;
              const isSelected = selectedId === id;
              const atPlayhead = focusedTimestamp != null && rowContainsPlayhead(row, focusedTimestamp);
              const isCorr = correlation?.relatedNetworkId === id;

              if (row.kind === 'network') {
                const { entry } = row;
                const status = entry.response?.status ?? 0;
                const failed = isNetworkError(status, entry.error);
                const errorSelected = isSelected && failed;
                return (
                  <tr
                    key={id}
                    data-row-id={id}
                    onClick={() => setSelectedId(id)}
                    className={cn(
                      isSelected && !errorSelected && 'selected',
                      errorSelected && 'bg-[var(--list-row-error-bg)] outline outline-1 outline-[var(--list-row-error-ring)]',
                      atPlayhead && !isSelected && 'bg-[var(--accent)]/8 ring-1 ring-inset ring-[var(--accent)]/25',
                      isCorr && 'bg-amber-950/20 ring-1 ring-inset ring-amber-400/30',
                    )}
                  >
                    <td className="tabular-nums text-[var(--text-muted)]">{formatSessionOffset(entry.timestamp)}</td>
                    <td>
                      <span className="rounded bg-[var(--bg-editor)] px-1.5 py-0.5 text-[10px] font-medium text-[var(--text-secondary)]">
                        {PAGE_CAPTURE_TYPE}
                      </span>
                    </td>
                    <td>
                      <span className="text-[10px] font-bold text-[var(--text-secondary)]">{entry.request.method}</span>
                    </td>
                    <td>
                      <span className={cn('status-pill', statusPillClass(status || 0))}>{status || 'ERR'}</span>
                    </td>
                    <td>
                      <span className="mono-small truncate" title={entry.request.url}>
                        {entry.request.url}
                      </span>
                    </td>
                    <td className="tabular-nums text-[var(--text-muted)]">{formatDuration(entry.timing.durationMs)}</td>
                  </tr>
                );
              }

              const { entry } = row;
              const resFailed = Boolean(entry.error) || entry.status >= 400;
              const errorSelected = isSelected && resFailed;
              return (
                <tr
                  key={id}
                  data-row-id={id}
                  onClick={() => setSelectedId(id)}
                  className={cn(
                    isSelected && !errorSelected && 'selected',
                    errorSelected && 'bg-[var(--list-row-error-bg)] outline outline-1 outline-[var(--list-row-error-ring)]',
                    atPlayhead && !isSelected && 'bg-[var(--accent)]/8 ring-1 ring-inset ring-[var(--accent)]/25',
                    isCorr && 'bg-amber-950/20 ring-1 ring-inset ring-amber-400/30',
                  )}
                >
                  <td className="tabular-nums text-[var(--text-muted)]">{formatSessionOffset(entry.timestamp)}</td>
                  <td>
                    <span className="rounded bg-[var(--bg-editor)] px-1.5 py-0.5 text-[10px] font-medium text-[var(--text-secondary)]">
                      {entry.resourceType}
                    </span>
                  </td>
                  <td>
                    <span className="text-[10px] font-bold text-[var(--text-secondary)]">{entry.method}</span>
                  </td>
                  <td>
                    <span className={cn('status-pill', statusPillClass(entry.status))}>{entry.status}</span>
                  </td>
                  <td>
                    <span className="mono-small truncate" title={entry.url}>
                      {entry.url}
                    </span>
                  </td>
                  <td className="tabular-nums text-[var(--text-muted)]">{formatDuration(entry.timing.durationMs)}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};
